import { useEffect, useMemo, useRef, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/arrow-down.svg';
import { assignMemberTags, getMember } from '../../../api/members';
import { getTagsList } from '../../../api/tags';
import { setMemberData } from '../../../store/memberSlice';
import { RootState } from '../../../store/store';
import { Tag } from '../../../types/apiGeneratedTypes';
import './MemberTagsSelect.scss';

interface TagOption {
  key: number;
  value: string;
  color: string;
}

const mapTagsToOptions = (tags: Tag[] = []): TagOption[] =>
  tags
    .filter((tag) => tag.id != null)
    .map((tag) => ({
      key: tag.id as number,
      value: tag.name,
      color: tag.color || '#238698',
    }));

const getTagIdsKey = (tags: TagOption[]) =>
  tags
    .map((tag) => tag.key)
    .sort((a, b) => a - b)
    .join(',');

const MemberTagsSelect = () => {
  const dispatch = useDispatch();
  const { memberData } = useSelector((state: RootState) => state.member);
  const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
  const [selectedTags, setSelectedTags] = useState<TagOption[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const lastSavedTagIdsKeyRef = useRef('');

  useEffect(() => {
    const fetchTags = async () => {
      setIsLoadingTags(true);
      try {
        const response = await getTagsList();
        const activeTags = (response.tags ?? []).filter(
          (tag) => tag.is_active !== false && tag.id != null
        );
        setTagOptions(mapTagsToOptions(activeTags));
      } catch (error) {
        console.error('Error fetching tags:', error);
        toast.error('Failed to load tags');
      } finally {
        setIsLoadingTags(false);
      }
    };

    fetchTags();
  }, []);

  useEffect(() => {
    if (isOpen) return;

    const syncedTags = mapTagsToOptions(memberData?.tags ?? []);
    setSelectedTags(syncedTags);
    lastSavedTagIdsKeyRef.current = getTagIdsKey(syncedTags);
  }, [memberData?.tags, memberData?.user_id, isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const isDisabled = useMemo(
    () => isLoadingTags || isUpdating || !memberData?.user_id,
    [isLoadingTags, isUpdating, memberData?.user_id]
  );

  const filteredOptions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return tagOptions;
    return tagOptions.filter((option) =>
      option.value.toLowerCase().includes(query)
    );
  }, [tagOptions, searchTerm]);

  const saveTags = async (tags: TagOption[]) => {
    if (!memberData?.user_id || isUpdating) return;

    setIsUpdating(true);

    try {
      await assignMemberTags({
        member_id: memberData.user_id,
        tag_ids: tags.map((tag) => tag.key),
      });

      const updatedMember = await getMember(memberData.user_id);
      dispatch(setMemberData(updatedMember));
      lastSavedTagIdsKeyRef.current = getTagIdsKey(tags);
      toast.success('Member tags updated successfully');
    } catch (error: any) {
      console.error('Error updating member tags:', error);
      const revertedTags = mapTagsToOptions(memberData?.tags ?? []);
      setSelectedTags(revertedTags);
      lastSavedTagIdsKeyRef.current = getTagIdsKey(revertedTags);
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.description ||
        error?.message ||
        'Failed to update member tags';
      toast.error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    if (!isOpen) return;

    setIsOpen(false);
    setSearchTerm('');

    const currentKey = getTagIdsKey(selectedTags);
    if (currentKey !== lastSavedTagIdsKeyRef.current) {
      saveTags(selectedTags);
    }
  };

  const handleTagToggle = (option: TagOption) => {
    setSelectedTags((prev) =>
      prev.some((tag) => tag.key === option.key)
        ? prev.filter((tag) => tag.key !== option.key)
        : [...prev, option]
    );
  };

  const handleTriggerClick = () => {
    if (isDisabled) return;
    if (isOpen) {
      handleClose();
    } else {
      setIsOpen(true);
    }
  };

  return (
    <div
      className={`member-tags-select ${isUpdating ? 'is-updating' : ''} ${
        isOpen ? 'is-open' : ''
      }`}
    >
      <OutsideClickHandler onOutsideClick={handleClose}>
        <div className='member-tags-select__label'>Tags</div>

        <button
          type='button'
          className='member-tags-select__trigger'
          disabled={isDisabled}
          onClick={handleTriggerClick}
        >
          <div className='member-tags-select__trigger-content'>
            <span className='member-tags-select__placeholder'>
              {isLoadingTags ? 'Loading tags...' : 'Select tags'}
            </span>
          </div>
          <ArrowDownIcon
            className={`member-tags-select__arrow ${isOpen ? 'is-open' : ''}`}
          />
        </button>

        {isOpen && (
          <div className='member-tags-select__dropdown'>
            <div className='member-tags-select__search-row'>
              <input
                ref={searchInputRef}
                type='text'
                className='member-tags-select__search'
                placeholder='Search tags...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div className='member-tags-select__options'>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isSelected = selectedTags.some(
                    (tag) => tag.key === option.key
                  );

                  return (
                    <button
                      key={option.key}
                      type='button'
                      className={`member-tags-select__option ${
                        isSelected ? 'is-selected' : ''
                      }`}
                      onClick={() => handleTagToggle(option)}
                    >
                      <span
                        className='member-tags-select__option-color'
                        style={{ backgroundColor: option.color }}
                      />
                      <span className='member-tags-select__option-label'>
                        {option.value}
                      </span>
                      <span className='member-tags-select__option-check'>
                        {isSelected ? (
                          <span className='member-tags-select__selected' >&#x2713;</span>
                        ) : (
                          <span className='member-tags-select__option-check-empty' />
                        )}
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className='member-tags-select__empty'>No tags found</div>
              )}
            </div>
          </div>
        )}
      </OutsideClickHandler>
    </div>
  );
};

export default MemberTagsSelect;
